import { z } from 'zod';

export type FormatterFn = (value: string) => string;

export type DisplayFormat =
  | 'text'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'datetime-tz'
  | 'number'
  | 'percentage'
  | 'phone'
  | 'ssn'
  | 'masked'
  | 'html'
  | 'boolean'
  | 'file'
  | 'action-link'
  | 'status'
  | 'badge'
  | 'multiline'
  | 'multiline-list';

export type BadgeVariantHint =
  | 'success'
  | 'secondary'
  | 'error'
  | 'default'
  | 'default-outline'
  | 'warning'
  | 'info';

export type StatusTone = 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'primary';

export interface StatusEntry {
  readonly icon?: string;
  readonly tone: StatusTone;
  /** Display label override. When set, this text is shown instead of the raw value. */
  readonly label?: string;
}

/**
 * Maps string values to visual status indicators (icon + color tone).
 * Used with `displayFormat: 'status'`. The `_default` key is used
 * when the value doesn't match any other key.
 */
export type StatusMap = Readonly<Record<string, StatusEntry>>;

export type AdapterHint =
  | 'text'
  | 'password'
  | 'number'
  | 'textarea'
  | 'select'
  | 'async-select'
  | 'multi-select'
  | 'date'
  | 'time'
  | 'checkbox'
  | 'toggle'
  | 'radio'
  | 'phone'
  | 'file'
  | 'richtext'
  | 'otp'
  | 'custom';

export interface SelectOption {
  readonly label: string;
  readonly value: string | boolean;
}

/**
 * Micro-layout width for form controls (input shell), independent of matrix column span.
 * Macro placement uses matrix-solver; controlWidth caps how wide the control renders inside its cell.
 */
export type ControlWidth = 'auto' | 'short' | 'medium' | 'long' | 'full';

export interface FieldDef<T extends z.ZodTypeAny = z.ZodTypeAny> {
  readonly label: string;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly schema: T;
  readonly adapterHint: AdapterHint;
  /** Caps control width inside the grid cell. Defaults from adapterHint when omitted. */
  readonly controlWidth?: ControlWidth;
  readonly formatter?: FormatterFn | FormatterFn[];
  readonly displayFormat?: DisplayFormat;
  /** Static options for radio/select fields. Defined once in the domain, auto-injected by the form engine. */
  readonly options?: readonly SelectOption[];
  /** Fixed decimal places for numeric display (currency, percentage, number). Used by formatForDisplay. */
  readonly displayPrecision?: number;
  readonly emptyText?: string;
  /** Maps values to visual indicators when `displayFormat` is `'status'`. */
  readonly statusMap?: StatusMap;
  /** Badge variant hint when `displayFormat` is `'badge'`. Defaults to 'default'. */
  readonly badgeVariant?: BadgeVariantHint;
  /** Template string for badge text. Use `{value}` as placeholder. E.g., 'Cost Center #{value}'. */
  readonly badgeTemplate?: string;
  /**
   * Default adapter props for this field. Merged as a baseline whenever this `FieldDef` is used —
   * block-level `adapterProps` always wins over these. Use this to co-locate presentation hints
   * that are intrinsic to the field itself (e.g. `tag: 'DAYS'`, `tag: '%'`, `tag: 'YEAR'`),
   * so every consumer gets the correct appearance without copy-pasting `adapterProps`.
   *
   * @example
   * ```ts
   * export const electionWindowStart = define({
   *   label: 'Election Window Starts',
   *   adapterHint: 'number',
   *   adapterProps: { tag: 'DAYS' },
   *   ...
   * });
   * ```
   */
  readonly adapterProps?: Readonly<Record<string, unknown>>;
}

export const YES_NO_OPTIONS: readonly SelectOption[] = Object.freeze([
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
]);

export function field<T extends z.ZodTypeAny>(
  _domain: string,
  def: FieldDef<T>
): Readonly<FieldDef<T>> {
  return Object.freeze(def);
}

/**
 * Creates a domain-scoped field factory. Declare once per file, use for all fields.
 * CI verifies the domain arg matches the host filename stem.
 *
 * ```ts
 * const define = createDomainField('award');
 * export const grantDate = define({ label: 'Grant Date', ... });
 * ```
 */
export function createDomainField(_domain: string) {
  return <T extends z.ZodTypeAny>(def: FieldDef<T>): Readonly<FieldDef<T>> => Object.freeze(def);
}

/** Runtime factory for dynamic / user-defined fields (UDF). Same as field() but named for clarity. */
export const createFieldDef = field;

/** Hidden state field — stores derived values (e.g. flags from effects) without rendering UI. */
export const HIDDEN_FIELD: Readonly<FieldDef> = Object.freeze({
  label: '',
  schema: z.any(),
  adapterHint: 'text' as AdapterHint,
});
